"use client";

import { ReactNode, useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/use-fetch";
import { Badge, Button, Card, EmptyState, Input, Modal, PageHeader, Skeleton } from "@/components/ui";
import { useToast } from "@/components/toast";
import type { Account, SourceAddr } from "@/lib/types";

interface SenderIdRequest {
  senderIdRequestId: number;
  sourceAddr: string;
  reason: string | null;
  status: "PENDING" | "APPROVED" | "REJECTED" | string;
  reviewNote: string | null;
  reviewedAt: string | null;
  createdDate: string;
}

function reqTone(s: string): "amber" | "green" | "red" | "gray" {
  if (s === "PENDING") return "amber";
  if (s === "APPROVED") return "green";
  if (s === "REJECTED") return "red";
  return "gray";
}

function SenderIdRequests({ assigned }: { assigned: string[] }) {
  const toast = useToast();
  const [rows, setRows] = useState<SenderIdRequest[] | null>(null);
  const [sourceAddr, setSourceAddr] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api<{ requests: SenderIdRequest[] }>("account/sender-id-requests")
      .then((r) => setRows(r.requests || []))
      .catch(() => setRows([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Does the typed sender ID already exist for this account? (case-insensitive)
  const trimmed = sourceAddr.trim();
  const key = trimmed.toLowerCase();
  const alreadyAssigned = key.length > 0 && assigned.some((s) => s.trim().toLowerCase() === key);
  const alreadyPending = key.length > 0 && (rows ?? []).some((r) => r.status === "PENDING" && r.sourceAddr.trim().toLowerCase() === key);
  const duplicate = alreadyAssigned
    ? "You already have this sender ID."
    : alreadyPending
      ? "You already have a pending request for this sender ID."
      : null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const value = sourceAddr.trim();
    if (!value) {
      toast.error("Enter a sender ID.");
      return;
    }
    if (duplicate) {
      toast.error(duplicate);
      return;
    }
    setSaving(true);
    try {
      await api("account/sender-id-requests", { method: "POST", body: { sourceAddr: value, reason: reason.trim() || null } });
      toast.success("Request submitted for approval.");
      setSourceAddr("");
      setReason("");
      load();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card title="Request a sender ID">
      <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
        <div className="w-full sm:w-48">
          <Input label="Sender ID" value={sourceAddr} onChange={(e) => setSourceAddr(e.target.value.slice(0, 50))} placeholder="e.g. MyBrand" />
        </div>
        <div className="min-w-[12rem] flex-1">
          <Input label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="How you'll use it" />
        </div>
        <Button type="submit" disabled={saving || !trimmed || Boolean(duplicate)}>{saving ? "Submitting…" : "Request"}</Button>
      </form>
      {duplicate ? (
        <p className="mt-2 text-xs text-danger-600 dark:text-danger-400">{duplicate}</p>
      ) : (
        <p className="mt-2 text-xs text-gray-400">A sender ID (the “from” name, up to 11 characters recommended) must be approved before you can send with it.</p>
      )}

      <div className="mt-4">
        {rows === null ? (
          <div className="space-y-2">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-9" />)}</div>
        ) : rows.length === 0 ? (
          <EmptyState title="No requests yet" description="Request a sender ID above and we'll review it." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-4 font-medium">Sender ID</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Note</th>
                  <th className="py-2 pr-4 font-medium">Requested</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.senderIdRequestId} className="border-b border-gray-100">
                    <td className="py-2 pr-4 font-medium text-gray-900">{r.sourceAddr}</td>
                    <td className="py-2 pr-4"><Badge tone={reqTone(r.status)}>{r.status}</Badge></td>
                    <td className="py-2 pr-4 text-gray-600">{r.reviewNote || (r.status === "PENDING" ? "Awaiting review" : "—")}</td>
                    <td className="py-2 pr-4 text-xs text-gray-500">{new Date(r.createdDate).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}

interface UserRequest {
  userRequestId: number;
  requestedUsername: string;
  requestedDisplayName: string | null;
  status: "PENDING" | "APPROVED" | "REJECTED" | string;
  reviewNote: string | null;
  createdDate: string;
}

function NewUserRequests() {
  const toast = useToast();
  const [rows, setRows] = useState<UserRequest[] | null>(null);
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api<{ requests: UserRequest[] }>("account/user-requests")
      .then((r) => setRows(r.requests || []))
      .catch(() => setRows([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!username.trim()) {
      toast.error("Enter a username for the new user.");
      return;
    }
    if (mobile.replace(/\D/g, "").length < 7) {
      toast.error("Enter a valid mobile number.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.error("Enter a valid email address.");
      return;
    }
    setSaving(true);
    try {
      await api("account/user-requests", {
        method: "POST",
        body: { username: username.trim(), displayName: displayName.trim() || null, mobile: mobile.trim(), email: email.trim(), reason: reason.trim() || null }
      });
      toast.success("Request submitted for approval.");
      setUsername("");
      setDisplayName("");
      setMobile("");
      setEmail("");
      setReason("");
      load();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card title="Request a new user account">
      <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
        <Input label="Username" value={username} onChange={(e) => setUsername(e.target.value.slice(0, 100))} placeholder="e.g. acme.ops" required />
        <Input label="Display name (optional)" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g. Acme Operations" />
        <Input label="Mobile" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 15))} placeholder="2526xxxxxxx" inputMode="tel" required />
        <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" required />
        <Input label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why this account is needed" />
        <div className="sm:col-span-2">
          <Button type="submit" disabled={saving || !username.trim()}>{saving ? "Submitting…" : "Request account"}</Button>
        </div>
      </form>
      <p className="mt-2 text-xs text-gray-400">An admin reviews the request and, once approved, provisions the account and shares its temporary password with you.</p>

      <div className="mt-4">
        {rows === null ? (
          <div className="space-y-2">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-9" />)}</div>
        ) : rows.length === 0 ? (
          <EmptyState title="No requests yet" description="Request a new user account above." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-4 font-medium">Username</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Note</th>
                  <th className="py-2 pr-4 font-medium">Requested</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.userRequestId} className="border-b border-gray-100">
                    <td className="py-2 pr-4 font-medium text-gray-900">{r.requestedUsername}</td>
                    <td className="py-2 pr-4"><Badge tone={reqTone(r.status)}>{r.status}</Badge></td>
                    <td className="py-2 pr-4 text-gray-600">{r.reviewNote || (r.status === "PENDING" ? "Awaiting review" : "—")}</td>
                    <td className="py-2 pr-4 text-xs text-gray-500">{new Date(r.createdDate).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}

function ChangePassword() {
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      await api("account/password", { method: "PUT", body: { currentPassword: current, password: next } });
      toast.success("Password changed.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card title="Change password">
      <form onSubmit={submit} className="space-y-3">
        <Input label="Current password" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" required />
        <Input label="New password" type="password" value={next} onChange={(e) => setNext(e.target.value)} hint="At least 8 characters." autoComplete="new-password" required />
        <Input label="Confirm new password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" required />
        <Button type="submit" disabled={saving || !current || !next}>{saving ? "Saving…" : "Update password"}</Button>
      </form>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-gray-100 py-2.5 last:border-0">
      <dt className="text-sm text-gray-500">{label}</dt>
      <dd className="text-sm font-medium text-gray-900">{children}</dd>
    </div>
  );
}

function TwoFactor({ enabled, onChanged }: { enabled: boolean; onChanged: () => void }) {
  const toast = useToast();
  const [enableOpen, setEnableOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [step, setStep] = useState<"send" | "code">("send");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  function closeEnable() {
    setEnableOpen(false);
    setStep("send");
    setCode("");
    setSentTo(null);
  }

  async function sendCode() {
    setBusy(true);
    try {
      const r = await api<{ sentTo: string }>("account/two-factor/otp-request", { method: "POST" });
      setSentTo(r.sentTo);
      setStep("code");
      toast.success(`Code sent to ${r.sentTo}.`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function confirmEnable() {
    setBusy(true);
    try {
      await api("account/two-factor", { method: "PUT", body: { enabled: true, code: code.trim() } });
      toast.success("Two-factor authentication enabled.");
      closeEnable();
      onChanged();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function confirmDisable() {
    setBusy(true);
    try {
      await api("account/two-factor", { method: "PUT", body: { enabled: false, currentPassword: password } });
      toast.success("Two-factor authentication disabled.");
      setDisableOpen(false);
      setPassword("");
      onChanged();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Two-factor authentication">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-gray-600">Require a one-time code sent to your mobile every time you sign in.</p>
          <div className="mt-2">{enabled ? <Badge tone="green">Enabled</Badge> : <Badge tone="gray">Disabled</Badge>}</div>
        </div>
        {enabled ? (
          <Button variant="secondary" onClick={() => setDisableOpen(true)}>Disable</Button>
        ) : (
          <Button onClick={() => { setEnableOpen(true); setStep("send"); }}>Enable</Button>
        )}
      </div>

      <Modal
        open={enableOpen}
        onClose={closeEnable}
        title="Enable two-factor authentication"
        footer={
          step === "send" ? (
            <>
              <Button variant="secondary" onClick={closeEnable}>Cancel</Button>
              <Button onClick={sendCode} disabled={busy}>{busy ? "Sending…" : "Send code"}</Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={closeEnable}>Cancel</Button>
              <Button onClick={confirmEnable} disabled={busy || !code.trim()}>{busy ? "Enabling…" : "Enable"}</Button>
            </>
          )
        }
      >
        {step === "send" ? (
          <p className="text-sm text-gray-600">
            We&apos;ll send a verification code to your registered mobile number to confirm you can receive codes.
          </p>
        ) : (
          <>
            <p className="mb-3 text-sm text-gray-600">Enter the code sent to {sentTo}.</p>
            <Input label="Verification code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} inputMode="numeric" autoFocus />
          </>
        )}
      </Modal>

      <Modal
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        title="Disable two-factor authentication"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDisableOpen(false)}>Cancel</Button>
            <Button variant="danger" onClick={confirmDisable} disabled={busy || !password}>{busy ? "Disabling…" : "Disable"}</Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-gray-600">Enter your password to turn off two-factor authentication.</p>
        <Input label="Current password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
      </Modal>
    </Card>
  );
}

export default function AccountPage() {
  const { data: account, loading, reload: reloadAccount } = useFetch<Account>("account");
  const { data: sendersData } = useFetch<{ sourceAddrs: SourceAddr[] }>("auth/source-addrs");
  const senders = sendersData?.sourceAddrs ?? [];
  const prepaid = account?.accountType !== "POSTPAID";
  const low = prepaid && account ? account.creditBalance <= (account.lowCreditThreshold ?? 0) : false;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader title="My Account" subtitle="Your profile, balance and sending permissions." />

      <div className="grid gap-6 sm:grid-cols-2">
        <Card title="Profile">
          {loading ? (
            <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-6" />)}</div>
          ) : (
            <dl>
              <Row label="Name">{account?.displayName || "—"}</Row>
              <Row label="Username">{account?.username}</Row>
              <Row label="Account type">
                <Badge tone={prepaid ? "blue" : "gray"}>{prepaid ? "Prepaid" : "Postpaid"}</Badge>
              </Row>
              <Row label="Owner mobile">{account?.ownerMsisdn || "—"}</Row>
            </dl>
          )}
        </Card>

        <Card title="Balance & limits">
          {loading ? (
            <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-6" />)}</div>
          ) : (
            <dl>
              <Row label={prepaid ? "SMS credit" : "Billing"}>
                {prepaid ? (
                  <span className={low ? "text-danger-600 dark:text-danger-400" : ""}>{(account?.creditBalance ?? 0).toLocaleString()}</span>
                ) : (
                  "Postpaid (per usage)"
                )}
              </Row>
              {prepaid && <Row label="Low-credit alert at">{(account?.lowCreditThreshold ?? 0).toLocaleString()}</Row>}
              <Row label="International SMS">
                {account?.allowInternational ? <Badge tone="green">Enabled</Badge> : <Badge tone="gray">Local only</Badge>}
              </Row>
              <Row label="Two-way SMS">
                {account?.allowTwoWay ? <Badge tone="green">Enabled</Badge> : <Badge tone="gray">Off</Badge>}
              </Row>
            </dl>
          )}
        </Card>
      </div>

      <Card title="Sender IDs">
        {senders.length === 0 ? (
          <p className="text-sm text-gray-500">No sender IDs assigned yet. Contact your account manager to add one.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {senders.map((s) => (
              <Badge key={s.sourceAddr} tone="blue">{s.sourceAddr}</Badge>
            ))}
          </div>
        )}
        <p className="mt-3 text-xs text-gray-400">These are the approved “from” names you can send with. Request a new one below.</p>
      </Card>

      <SenderIdRequests assigned={senders.map((s) => s.sourceAddr)} />

      <NewUserRequests />

      {account && <TwoFactor enabled={Boolean(account.twoFactorEnabled)} onChanged={reloadAccount} />}

      <ChangePassword />
    </div>
  );
}
